/**
 * Issue #758 — step 2 of 3, the scorecard. Copy strings and small pure
 * helpers, pulled out of the component for the same reason
 * `logGroundStep.js` (#757) gives: this repo mounts no components in unit
 * tests, so anything worth pinning down directly lives here instead, where
 * a plain Jest test can read it with no DOM at all.
 *
 * Source: `DirectionA2.dc.html` (design-round-5, sha256 `1e29fb61…`), the
 * approved drawing for this step — its own inline copy is the source of
 * truth; where the issue text and the drawing differ, the drawing wins
 * (brief-758.md, Step 0).
 */
import { NOT_RECORDED, counterOrNull } from "./counters";
import { oversToBalls } from "./overs";

export const LOG_STEP2_BACK_LABEL = "Back";

export const GROUND_EYEBROW = "Ground";
export const CHANGE_LABEL = "Change";

export const MORE_TITLE = "Start time, format, season";
export const MORE_SUBLABEL = "Three more the app keeps. Season suggests from your own history.";

// Issue #996 (D201, overlay ruling, Andreas 2026-09-30, from Martin's
// log-flow canvas comments) — "Suggests as you type" read as a promise
// about behaviour, not a field name. The plain field name instead, the
// same shape OPP_PLACEHOLDER ("Team name") already gives the Opposition
// field right below it. Flag-on only (LogMatchScorecardStep.jsx:461,
// behind newUi("log-scorecard")); the legacy dialog's own Competition
// placeholder (PerformanceTracking.jsx, "e.g. Thornmere Shield") is untouched.
export const COMP_HELPER = "Competition name";
export const OPP_PLACEHOLDER = "Team name";

export const BATTING_LABEL = "Batting";
export const STRIKE_RATE_LABEL = "Strike rate, worked out for you";
// The static half of the strike-rate help line — always shown, regardless
// of whether balls faced is filled in. `strikeRateHelpText` below prepends
// the dynamic "Rests on N off M." clause in front of this when there is a
// real divisor to name.
export const STRIKE_RATE_CODA =
  "Leave the balls blank and there is nothing to divide by, so there is no strike rate — not a nought.";

export const BOWLING_LABEL = "Bowling";
// D153 (#884, Andreas, 2026-09-25, decisions-2026-09-24.md): the bowling
// runs counter's own label reads "Runs conceded" — the drawn "Runs" alone
// (DirectionA2.dc.html:111) invites confusion with the batting section's
// own "Runs" a few lines above, and copy-check cannot see this drift
// (the drawn "Runs" survives as a substring of "Runs conceded", and
// unchanged verbatim on the batting counter besides).
export const RUNS_CONCEDED_LABEL = "Runs conceded";
export const ECONOMY_LABEL = "Economy, worked out for you";
// PR #841 review — the drawn main state has bowling blank, and this exact
// paragraph (including its own hypothetical example, "Bowl four for
// eighteen…") is what renders there, byte-identical: that IS the
// conformance state. `economyHelpText` below swaps this out for a short
// dynamic "Rests on N overs." line once overs are actually entered — the
// drawing's own coda promises exactly that ("…with the line saying it
// rests on four overs").
export const ECONOMY_HELP =
  "Didn't bowl? Leave it. No overs means there is nothing to divide by, so there is no economy here — blank, not nought. Bowl four for eighteen and it fills in, with the line saying it rests on four overs.";

export const FIELDING_LABEL = "Fielding";
export const FIELDING_HELP = "These four are the fielder's. What you took behind the stumps is counted on its own, below.";

export const KEEPER_LABEL = "I was the wicketkeeper";
// Issue #704 — true now: the tick is the flag "matches kept" and
// "dismissals / match" actually count on, not a display-only toggle. The
// note that used to sit beside it ("Whether you kept isn't recorded yet…")
// is gone because that limit is closed.
export const KEEPER_SUBLABEL = "Only ticked games count as matches kept";

export const KEEPER_GROUP_LABEL = "Behind the stumps";
export const KEEPER_GROUP_HELP =
  "Your own counters, not the fielder's — a keeper's run out and a fielder's run out are two different things and the record keeps them apart. A catch behind is not a fielding catch either.";

// Build-only extra #2 (#715 open) — one full-width line per group (PR #841
// review: not one beside each of the two NOT-NULL-DEFAULT-0 catches boxes,
// which crowded the box and never reached the drawn width), placed under
// that group's own counter grid, directly above its existing help line —
// in Fielding, before `FIELDING_HELP`; in Behind the stumps, before
// `KEEPER_GROUP_HELP`.
export const CATCHES_ZERO_NOTE = "Catches are stored as 0 rather than blank for now (#715).";

export const FOOTER_NOTE =
  "Every box you don't touch stays empty. Empty is stored as nothing, never as a nought — a nought would say you fielded and took none, and the record won't say what you didn't.";

export const SAVE_LABEL = "Put it on the record";
export const SAVE_LABEL_EDIT = "Update match";
export const SAVING_LABEL = "Saving…";

export const REQUIRED_SUMMARY = "Please fill in the required fields, highlighted below";
export const SAVE_FAILED_TITLE = "Failed to save match";
export const SAVE_FAILED_BODY = "Everything you typed is still here. Try again, or change what the message names.";

// Issue #1150 (D225 item 6) — finishing a planned match, drawn in
// `PlanMatchAfter.dc.html` (round 10): the same step, headed "How did it go?".
export const FINISH_EYEBROW = "Log a match";
export const FINISH_TITLE = "How did it go?";
export const FINISH_LEDE = "Date, opposition, ground, team and competition come from the planned match; you add your figures.";
export const SAVE_LABEL_FINISH = "Save the match";
// The board draws ONE line (D259): the banner when the API gave no reason, else the
// API's own words in the banner and this sentence under it.
export const FINISH_SAVE_FAILED_BODY = "Your match is still planned \u2014 try again.";
export const FINISH_SAVE_FAILED = `That didn't save. ${FINISH_SAVE_FAILED_BODY}`;

export const EDIT_EYEBROW = "Editing a match";
export const EDIT_TITLE = "Update match performance";

// Issue #1028 review, F6 — round 1 of this issue added FACILITY_LABEL/
// FACILITY_OPTIONAL/FACILITY_STORED_LABEL/FACILITY_STORED_NOTE here as a
// "pin", but nothing ever imported them: `FacilityPicker.jsx` renders its
// own hardcoded copy (shared with the training form, #166) and always
// did. A constant nothing reads and a test that only imports it back
// proves the import resolved, not that the drawn copy is what renders —
// theatre, not a pin. Deleted rather than wired through: `FacilityPicker`
// is the shared control for two screens with the SAME words today, and
// threading four props through it to satisfy a constant on one screen's
// own module would be indirection for no behavioural difference. If the
// two screens' copy ever needs to diverge, that is the moment for real
// props, not before.

/**
 * The strike-rate help line — `DirectionA2.dc.html:103`.
 *
 * The drawn main state (captured with 34 runs off 41 balls) shows BOTH the
 * dynamic fact ("Rests on 34 off 41.") and the static coda in the same
 * paragraph, so both must render together whenever there is a real
 * divisor. When there is none, the dynamic clause has nothing to say and
 * only the coda remains — still a complete sentence on its own.
 *
 * PR #841 review, final round — "has a divisor" is `counterOrNull(balls)`
 * truthy, the SAME test `rateOrNull` (`lib/counters.js`) itself uses
 * (`if (!d) return null`), not merely "the field isn't blank". A typed
 * `0` is not a divisor either way: `rateOrNull` already answers `null` for
 * it (nothing to divide by), and the earlier version of this function
 * disagreed with that — a blank-looking dash above a line that still said
 * "Rests on N off 0."
 */
export function strikeRateHelpText(runsDisplay, ballsDisplay) {
  const balls = counterOrNull(ballsDisplay);
  if (!balls) return STRIKE_RATE_CODA;
  const runs = runsDisplay === null || runsDisplay === undefined || runsDisplay === "" ? "0" : String(runsDisplay);
  return `Rests on ${runs} off ${ballsDisplay}. ${STRIKE_RATE_CODA}`;
}

/**
 * The economy help line — `DirectionA2.dc.html:125`.
 *
 * PR #841 review — with no overs entered (the drawn main state, and the
 * conformance capture), this is the fixed paragraph above, byte-identical.
 * With overs entered, the drawing's own coda promises a short dynamic line
 * instead ("…with the line saying it rests on four overs") — not the long
 * paragraph with a clause prepended (unlike strike rate's help line, whose
 * only drawn reference state has BOTH the dynamic clause and the static
 * coda together): no drawn state shows economy WITH overs, so this follows
 * the reviewer's own explicit instruction for that case rather than
 * inventing a longer combination nothing has verified.
 *
 * PR #841 review, final round — "has a divisor" is `counterOrNull(overs)`
 * truthy, the same test `strikeRateHelpText` and `rateOrNull` itself use:
 * a typed `0` falls back to the drawn no-divisor paragraph, same as blank.
 * Singular/plural on the unit word: "1 over", never "1 overs" — decided
 * from the PARSED number, not the raw string, so "1.0" still reads "over".
 */
export function economyHelpText(oversDisplay) {
  const overs = counterOrNull(oversDisplay);
  if (!overs) return ECONOMY_HELP;
  // #860: overs that are not over.ball notation have no economy, so the line
  // must not claim they are its base.
  if (oversToBalls(overs) === null) return "";
  const unit = overs === 1 ? "over" : "overs";
  return `Rests on ${oversDisplay} ${unit}.`;
}

/**
 * PR #841 review, D102 (decisions-2026-09-24.md, overlay at `5a12da6`) —
 * strike rate and economy display at a FIXED decimal count on this rebuilt
 * screen: one decimal for strike rate (the drawn main state reads "82.9",
 * not `rateOrNull`'s own two-decimal "82.93" — the earlier build's copy
 * check only passed because "82.9" is a SUBSTRING of "82.93", a coincidence
 * this formatter removes rather than relies on), two for economy. Display
 * only: `rateOrNull` (`lib/counters.js`) is untouched — `counters.test.js`
 * pins its own two-decimal round-trip, and the OLD dialog and the STORED
 * `strike_rate`/`economy_rate` values both keep using it exactly as
 * before. `value` is whatever `rateOrNull` returned (a number, already
 * rounded to 2dp) or `null`; this only re-renders it at the requested
 * precision, or the shared dash when there was nothing to divide by.
 */
export function formatRateDisplay(value, decimals) {
  if (value === null || value === undefined || value === "") return NOT_RECORDED;
  return Number(value).toFixed(decimals);
}
